import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PublicContentLayout } from "@/components/public/PublicContentLayout";
import { PUBLIC_ROUTES } from "@/routes/public";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  getPublicCampaign,
  claimCampaignVoucher,
  storePendingVoucher,
  type PublicCampaign,
} from "@/services/api/publicCampaigns";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Gift, ArrowRight, Copy, Tag } from "lucide-react";

export default function CampaignClaimPage() {
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const { isAuthenticated } = useAuth();
  const locale = useRouteLocale();

  const [campaign, setCampaign] = useState<PublicCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [voucherCode, setVoucherCode] = useState<string | null>(null);
  const [thankYouMessage, setThankYouMessage] = useState<string | null>(null);
  const [ctaUrl, setCtaUrl] = useState<string | null>(null);
  const [ctaText, setCtaText] = useState<string | null>(null);

  // JUB-188: the tab names the campaign; an unknown campaign reads as a not-found page and is not indexed.
  usePageMeta({
    title: campaign ? `${campaign.name} | Jubiel` : error ? t("httpErrors.404.metaTitle") : `${t("campaign.specialOffer")} | Jubiel`,
    description: campaign?.name ?? t("campaign.specialOffer"),
    locale,
    path: window.location.pathname,
    noindex: !!error,
  });

  useEffect(() => {
    if (uuid) {
      loadCampaign();
    }
  }, [uuid]);

  const loadCampaign = async () => {
    if (!uuid) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getPublicCampaign(uuid);
      if (data.type !== "access") {
        setError(t("campaign.notAccess"));
        return;
      }
      setCampaign(data);
    } catch (err: unknown) {
      console.error("Failed to load campaign:", err);
      // JUB-188: never surface the backend message here, it is English only.
      setError(t("campaign.notFound"));
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!uuid) return;

    try {
      setClaiming(true);
      const result = await claimCampaignVoucher(uuid);

      setVoucherCode(result.code);
      setThankYouMessage(result.thankYouMessage);
      setCtaUrl(result.ctaUrl);
      setCtaText(result.ctaText);
      setClaimed(true);

      // If user is not authenticated, store in localStorage
      if (!result.assigned) {
        storePendingVoucher(uuid, result.code);
      }

      toast.success(t("campaign.claimSuccess"));
    } catch (err: unknown) {
      console.error("Failed to claim voucher:", err);
      const errorMessage = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      toast.error(errorMessage || t("campaign.claimFailed"));
    } finally {
      setClaiming(false);
    }
  };

  const copyToClipboard = () => {
    if (voucherCode) {
      navigator.clipboard.writeText(voucherCode);
      toast.success(t("common.copied"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <PublicContentLayout>
        <div className="py-16 text-center space-y-6">
          <Card className="max-w-md w-full mx-auto">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground" data-testid="campaign-error">{error}</p>
            </CardContent>
          </Card>
          <Link to={PUBLIC_ROUTES.home(locale)}>
            <Button variant="outline">{t("httpErrors.404.backHome")}</Button>
          </Link>
        </div>
      </PublicContentLayout>
    );
  }

  if (!campaign) {
    return null;
  }

  // Success state
  if (claimed && voucherCode) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="campaign-success">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">{t("campaign.claimedTitle")}</CardTitle>
            {thankYouMessage && (
              <CardDescription className="text-base" data-testid="campaign-thank-you">{thankYouMessage}</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">{t("campaign.voucherCodeLabel")}</p>
              <div className="flex items-center justify-center gap-2">
                <code className="text-2xl font-bold tracking-wider" data-testid="campaign-voucher-code">{voucherCode}</code>
                <Button variant="ghost" size="sm" onClick={copyToClipboard}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {!isAuthenticated && (
              <p className="text-sm text-amber-600 text-center">
                {t("campaign.loginToSave")}
              </p>
            )}

            {ctaUrl && ctaText && (
              <Button className="w-full" onClick={() => window.location.href = ctaUrl}>
                {ctaText}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Claim page
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-pink-100 flex items-center justify-center">
            <Tag className="h-8 w-8 text-pink-600" />
          </div>
          <CardTitle className="text-2xl">{campaign.name}</CardTitle>
          <CardDescription className="text-base">
            {t("campaign.claimIntro")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-muted rounded-lg p-4 text-center">
            <Gift className="h-8 w-8 mx-auto mb-2 text-pink-500" />
            <p className="text-sm text-muted-foreground">
              {t("campaign.discountWaiting")}
            </p>
          </div>

          <Button
            onClick={handleClaim}
            disabled={claiming}
            data-testid="campaign-claim-button"
            size="lg"
            className="w-full"
          >
            {claiming ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("campaign.claiming")}
              </>
            ) : (
              <>
                <Gift className="h-4 w-4 mr-2" />
                {t("campaign.claimButton")}
              </>
            )}
          </Button>

          {!isAuthenticated && (
            <p className="text-xs text-muted-foreground text-center">
              {t("campaign.loginFirst")}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
